
// =============================================================
// LIVE SYNC — instant cross-surface refresh signalling.
// =============================================================
// The app already polls Supabase (the single source of truth) every 10–15s on
// every surface. This module makes changes propagate INSTANTLY between surfaces
// open in the same browser (e.g. admin in one tab, the user/brand/partner in
// another) by broadcasting a tiny "data changed" ping the moment an admin
// mutates data. Every listener then re-hydrates immediately instead of waiting
// for its next poll. The periodic poll remains the backstop for cross-device.
//
// Uses BroadcastChannel when available, with a localStorage-event fallback so
// it still works in browsers/contexts without BroadcastChannel.
// =============================================================

/** What changed — lets listeners re-hydrate only what they care about. */
export type SyncKind =
  | 'drops'
  | 'questions'
  | 'partnerCodes'
  | 'users'
  | 'catches'
  | 'reviews'
  | 'all'

const CHANNEL = 'gonefishing-live-sync'
const LS_KEY = 'gonefishing:lastSync'

let channel: BroadcastChannel | null = null
const hasBC = typeof window !== 'undefined' && 'BroadcastChannel' in window
if (hasBC) {
  try {
    channel = new BroadcastChannel(CHANNEL)
  } catch {
    channel = null
  }
}

/** Broadcast that `kind` of data just changed so other surfaces refresh now. */
export function notifyDataChanged(kind: SyncKind = 'all'): void {
  const payload = { kind, at: Date.now() }
  try {
    if (channel) channel.postMessage(payload)
    // localStorage fallback also reaches other tabs (fires a 'storage' event
    // in every OTHER tab). Harmless duplicate when BroadcastChannel also fires.
    if (typeof window !== 'undefined')
      window.localStorage.setItem(LS_KEY, JSON.stringify(payload))
  } catch {
    /* best-effort — never let sync signalling break a write */
  }
}

/**
 * Subscribe to data-changed pings from other surfaces. Returns an unsubscribe
 * function. The callback receives the SyncKind so it can refresh selectively.
 */
export function onDataChanged(cb: (kind: SyncKind) => void): () => void {
  const bcHandler = (e: MessageEvent) => {
    const kind = (e.data && (e.data.kind as SyncKind)) || 'all'
    cb(kind)
  }
  const lsHandler = (e: StorageEvent) => {
    if (e.key !== LS_KEY || !e.newValue) return
    try {
      const kind = (JSON.parse(e.newValue).kind as SyncKind) || 'all'
      cb(kind)
    } catch {
      cb('all')
    }
  }
  if (channel) channel.addEventListener('message', bcHandler)
  if (typeof window !== 'undefined')
    window.addEventListener('storage', lsHandler)
  return () => {
    if (channel) channel.removeEventListener('message', bcHandler)
    if (typeof window !== 'undefined')
      window.removeEventListener('storage', lsHandler)
  }
}
